import {
  React,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  X,
  Box,
  borderColor,
} from '../../utils/globalImports';
import { CustomButton, CircularProgress } from '../../utils/globalImports';

interface CustomConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmColorPalette?: 'primary' | 'secondary' | 'error' | 'warning' | 'info';
  cancelColorPalette?: 'primary' | 'secondary' | 'error' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  disableConfirm?: boolean;
  disableCancel?: boolean;
  isLoading?: boolean;
}

const dialogTextStyles: React.CSSProperties = {
  fontSize: '18px',
  textAlign: 'center',
  color: '#8c8c8c',
  lineHeight: '1.5',
  padding: '50px',
};

const dialogTitleStyles: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: 500,
  padding: '0px 0px 20px 0px',
  textAlign: 'center',
  color: '#475569',
};

const CustomConfirmDialog: React.FC<CustomConfirmDialogProps> = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  onClose,
  confirmColorPalette = 'primary',
  cancelColorPalette = 'primary',
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  disableConfirm = false,
  disableCancel = false,
  isLoading = false,
}) => {
  const isCustomConfirmLabel = !!confirmLabel;
  const isCustomCancelLabel = !!cancelLabel;
  return (
    <Dialog
      open={open}
      onClose={disableCancel ? () => {} : onClose}
      maxWidth="md"
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '25px',
          padding: '25px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          width: '32%',
          overflow: 'hidden',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={disableCancel}
        sx={{
          position: 'absolute',
          top: '25xpx',
          right: '12px',
          color: `${cancelColorPalette}.main`,
          padding: '4px',
          '& .MuiSvgIcon-root, & svg': {
            fontSize: '16px',
            strokeWidth: 5,
          },
        }}
      >
        <X strokeWidth={2.5} size={16} />
      </IconButton>

      <DialogTitle sx={{ ...dialogTitleStyles }}>{title}</DialogTitle>
      <Divider sx={{ width: '114.6%', marginLeft: '-10%', marginRight: '-10%' }} />
      <Box
        sx={{
          maxHeight: '300px',
          overflowY: 'auto',
        }}
      >
        <DialogContent
          sx={{
            ...dialogTextStyles,
            whiteSpace: 'pre-line',
          }}
        >
          {message}
        </DialogContent>
      </Box>
      <Divider sx={{ width: '114.6%', marginLeft: '-10%', marginRight: '-10%' }} />

      <DialogActions
        sx={{
          justifyContent: 'center',
          gap: '16px',
          mt: '20px',
        }}
      >
        <CustomButton
          onClick={onCancel}
          variant="outlined"
          disabled={disableCancel}
          color={cancelColorPalette}
          text={isCustomCancelLabel ? cancelLabel : 'Cancel'}
          sx={{
            minWidth: '150px',
            padding: '10px 30px',
            borderRadius: '50px',
          }}
        />

        <CustomButton
          onClick={onConfirm}
          disabled={disableConfirm}
          variant="contained"
          text={isCustomConfirmLabel ? confirmLabel : 'OK'}
          color={confirmColorPalette}
          sx={{
            minWidth: '150px',
            padding: '10px 30px',
            borderRadius: '50px',
          }}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        />
      </DialogActions>
    </Dialog>
  );
};

export default CustomConfirmDialog;
